import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp, limit } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { getFeatures, isBetaUser } from '../utils/features';
import {
    Search, Building2, MapPin, Map as MapIcon,
    CheckCircle, Filter, Eye, List,
    Lock, UserPlus, LogIn, Star, Image as ImageIcon,
    Handshake, ChevronLeft, ChevronRight
} from 'lucide-react';
import { isAdminUser } from '../utils/adminClaim';
import LocationConsentModal from './common/LocationConsentModal';
import PolicyNoticeBanner from './common/PolicyNoticeBanner';
import { POLICY_NOTICES, LBS_TERMS_V11_KEY } from '../config/policyNotices';
import {
    LOCATION_SERVICES, readLocationConsent, writeLocationConsent, clearLocationConsent, requestBrowserLocation,
    LOCATION_REVOKE_OK, LOCATION_REVOKE_FAIL, LOCATION_REGION_HINT,
} from '../utils/locationConsent';

const LOC_SERVICE = LOCATION_SERVICES.findLab.key;

function PublicLabDirectory({ user }) {
    // 단계별 오픈: 홍보페이지 CTA는 4차(매칭) 오픈 또는 베타만
    const [featureFlags, setFeatureFlags] = useState(null);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatureFlags(f); });
        return () => { alive = false; };
    }, []);
    const matchOpen = isBetaUser(featureFlags, user) || ((featureFlags?.phase || 1) >= 4);
    const navigate = useNavigate();
    const [labs, setLabs] = useState([]);
    const [filteredLabs, setFilteredLabs] = useState([]);
    const [visibleCount, setVisibleCount] = useState(30); // 페이징 — 초기 30개
    const LAB_PAGE_SIZE = 30;
    // LOCALDATA 미가입 치과기공소 (지도 마커만)
    const [unclaimedLabs, setUnclaimedLabs] = useState([]);
    const unclaimedMarkersRef = useRef([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterSpecialty, setFilterSpecialty] = useState('all');
    const [regionFilter, setRegionFilter] = useState('all');   // 시/도 필터
    const [sortBy, setSortBy] = useState('distance');          // distance | rating | reviews
    const [page, setPage] = useState(1);                        // 전체 기공소 페이지네이션 (20개/page)
    const [premiumPage, setPremiumPage] = useState(1);          // 프리미엄(회원) 페이지네이션 (12개/page)
    const [showMap, setShowMap] = useState(false);              // 전체화면 지도
    // 최신 리뷰 + 리뷰 작성칸
    const [recentReviews, setRecentReviews] = useState([]);
    const [reviewLabId, setReviewLabId] = useState('');
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewText, setReviewText] = useState('');
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [showSignupPrompt, setShowSignupPrompt] = useState(false);
    const [connectingLabId, setConnectingLabId] = useState(null);
    const [localFirst, setLocalFirst] = useState(true);
    const [userLocation, setUserLocation] = useState(null);
    const [viewMode, setViewMode] = useState('map'); // 'map' only
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef([]);
    const mapInitializedRef = useRef(false); // 첫 진입 시에만 자동 setCenter
    const [showResearchBtn, setShowResearchBtn] = useState(false);
    const slideRef = useRef(null);
    const touchStartX = useRef(0);

    // 실제 회원(치과/기공소)만 로그인으로 인정 — 익명/비회원 제외
    const isLoggedIn = !!user && !!user.uid && (user.businessType === 'dental' || user.businessType === 'lab' || user.businessType === 'outsource' || user.userType === 'staff');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // 🔒 접근 허용 조건 — 관리자 · 치과(대표·직원) · 비로그인자만
    const isAdminViewer = isAdminUser(user);
    const isDentalUser = user?.businessType === 'dental' || user?.businessType === 'clinic';
    // 직원: userType='staff'이면서 소속 기업(companyBusinessType)이 치과인 경우
    const isDentalStaff = user?.userType === 'staff' && (user?.companyBusinessType === 'dental' || user?.companyBusinessType === 'clinic' || user?.businessType === 'dental' || user?.businessType === 'clinic');
    const isGuest = !user?.uid;
    const canAccess = isGuest || isAdminViewer || isDentalUser || isDentalStaff;
    const isBlockedUser = !canAccess;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 위치 이용 — 화면 진입 시 자동 요청 없음(약관 v1.1 제3조 ③). [현재 위치로 찾기] → 안내창 동의 후에만 요청
    const [showLocConsent, setShowLocConsent] = useState(false);
    const [locating, setLocating] = useState(false);
    const [locNotice, setLocNotice] = useState('');       // 오류·철회·지역 검색 안내

    const askLocation = () => {
        setLocating(true);
        requestBrowserLocation(
            (loc) => { setUserLocation(loc); setLocating(false); setLocNotice(''); },
            (msg) => { setLocating(false); setLocNotice(msg); },
        );
    };
    const onLocationButton = () => {
        setLocNotice('');
        if (readLocationConsent(LOC_SERVICE)) { askLocation(); return; }
        setShowLocConsent(true);
    };
    const agreeLocConsent = () => { writeLocationConsent(LOC_SERVICE); setShowLocConsent(false); askLocation(); };
    const declineLocConsent = () => { setShowLocConsent(false); setLocNotice(LOCATION_REGION_HINT); };
    const revokeLocConsent = () => {
        const ok = clearLocationConsent();
        setUserLocation(null);
        setLocNotice(ok ? LOCATION_REVOKE_OK : LOCATION_REVOKE_FAIL);
    };

    // 거리 계산 (Haversine)
    const getDistance = useCallback((lat1, lng1, lat2, lng2) => {
        if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }, []);

    const formatDistance = (lab) => {
        if (!userLocation || !lab.lat || !lab.lng) return null;
        const d = getDistance(userLocation.lat, userLocation.lng, lab.lat, lab.lng);
        if (d === Infinity) return null;
        return d < 1 ? `${Math.round(d * 1000)}m` : `${d.toFixed(1)}km`;
    };

    const specialtyOptions = [
        'all', '크라운/브릿지', '임플란트', '임플란트 보철', '교정장치', '틀니',
        '라미네이트', '지르코니아', '금속도재', '올세라믹', '투명교정', '스플린트',
        'CAD/CAM 디지털 기공', '라미네이트/비니어', '임시 보철'
    ];

    const getServiceNames = (lab) => {
        if (Array.isArray(lab.services)) return lab.services.map(s => typeof s === 'string' ? s : s.name);
        if (lab.specialties) return lab.specialties;
        return [];
    };
    const getIntro = (lab) => lab.philosophy || lab.introduction || '';

    // 다양한 무료 아바타 서비스 — 단조롭지 않게 4가지 스타일 혼합
    const avInitials = (seed, color) => `https://ui-avatars.com/api/?name=${encodeURIComponent(seed)}&background=${color}&color=fff&bold=true&size=240&font-size=0.5`;
    const avShapes = (seed, bg) => `https://api.dicebear.com/7.x/shapes/svg?seed=${encodeURIComponent(seed)}&backgroundColor=${bg}`;
    const avIdenticon = (seed, bg) => `https://api.dicebear.com/7.x/identicon/svg?seed=${encodeURIComponent(seed)}&backgroundColor=${bg}`;
    const avThumbs = (seed) => `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(seed)}`;
    const avRings = (seed, bg) => `https://api.dicebear.com/7.x/rings/svg?seed=${encodeURIComponent(seed)}&backgroundColor=${bg}`;

    // 표시용 더미 기공소 20곳 — 다양한 데이터 + 다양한 아바타 스타일
    const DUMMY_LABS = [
        // 1) initials - 풀 데이터
        { id: 'dummy-1',  _dummy: true, businessName: '한성치과기공소',     profileImageUrl: avInitials('한성','6366f1'),  address: '서울특별시 강남구 테헤란로 152',  lat: 37.5172, lng: 127.0473, slogan: '20년 임플란트 보철 경력',          services: ['크라운/브릿지','임플란트 보철','지르코니아'],            priceTable: [{ item: '지르코니아 크라운', price: '180000', deliveryDays: 3 },{ item: 'PFM 크라운', price: '120000', deliveryDays: 3 }], compatibleScanners: ['Medit','3Shape TRIOS'],          monthlyCapacity: 1500, remakeRate: 1.2 },
        // 2) shapes - 슬로건 짧음
        { id: 'dummy-2',  _dummy: true, businessName: '서울덴탈랩',         profileImageUrl: avShapes('seoul','b6e3f4'),    address: '서울특별시 송파구 올림픽로 320',   lat: 37.5145, lng: 127.1059, slogan: '디지털 전문',                       services: ['CAD/CAM 디지털 기공','지르코니아','올세라믹','임플란트 보철','커스텀 어버트먼트'], priceTable: [{ item: '지르코니아 크라운', price: '170000', deliveryDays: 2 },{ item: '올세라믹', price: '230000', deliveryDays: 4 },{ item: '커스텀 어버트먼트', price: '85000', deliveryDays: 2 }], compatibleScanners: ['Primescan','3Shape TRIOS','iTero','Medit'], monthlyCapacity: 2000, remakeRate: 0.9 },
        // 3) identicon - 슬로건 없음
        { id: 'dummy-3',  _dummy: true, businessName: '명문치과기공소',     profileImageUrl: avIdenticon('myungmun','c0aede'), address: '서울특별시 강서구 마곡중앙로 50',  lat: 37.5509, lng: 126.8495, slogan: '',                                  services: ['임플란트 보철','크라운/브릿지','커스텀 어버트먼트'],         priceTable: [{ item: '임플란트 크라운', price: '220000', deliveryDays: 5 },{ item: '커스텀 어버트먼트', price: '90000', deliveryDays: 2 }], compatibleScanners: ['iTero','Medit'],                  monthlyCapacity: 1200, remakeRate: 1.5 },
        // 4) thumbs - 단가표 1개
        { id: 'dummy-4',  _dummy: true, businessName: '노원우리치과기공소', profileImageUrl: avThumbs('nowon'),                address: '서울특별시 노원구 동일로 1622',    lat: 37.6541, lng: 127.0567, slogan: '동북부 최대 규모',                  services: ['교정장치','투명교정','스플린트'],                          priceTable: [{ item: '투명교정', price: '350000', deliveryDays: 7 }],                                                                  compatibleScanners: ['Medit'],                          monthlyCapacity: 900,  remakeRate: 1.8 },
        // 5) rings - 큰 capacity 작은 remake
        { id: 'dummy-5',  _dummy: true, businessName: '영등포정밀기공',     profileImageUrl: avRings('yeongdeungpo','d1d4f9'), address: '서울특별시 영등포구 양평로 30',    lat: 37.5264, lng: 126.8961, slogan: '교정장치 특화 · 정밀 가공',         services: ['교정장치','투명교정','스플린트','라미네이트'],              priceTable: [{ item: '라미네이트', price: '280000', deliveryDays: 5 },{ item: '투명교정', price: '370000', deliveryDays: 8 }], compatibleScanners: ['3Shape TRIOS'],                   monthlyCapacity: 800,  remakeRate: 2.1 },
        // 6) initials - 단가표 2개, 풍부한 데이터
        { id: 'dummy-6',  _dummy: true, businessName: '강남DC덴탈',         profileImageUrl: avInitials('강남','ec4899'),  address: '서울특별시 강남구 논현로 425',     lat: 37.5114, lng: 127.0205, slogan: '심미보철 프리미엄',                 services: ['올세라믹','지르코니아','라미네이트/비니어'],                priceTable: [{ item: '올세라믹', price: '260000', deliveryDays: 4 },{ item: '라미네이트', price: '300000', deliveryDays: 5 }], compatibleScanners: ['Primescan','3Shape TRIOS','iTero'], monthlyCapacity: 1700, remakeRate: 0.8 },
        // 7) shapes - 슬로건 길게
        { id: 'dummy-7',  _dummy: true, businessName: '인천남동치기공소',   profileImageUrl: avShapes('namdong','ffd5dc'),     address: '인천광역시 남동구 구월로 220',     lat: 37.4486, lng: 126.7314, slogan: '인천 1세대 기공소 · 30년 노하우',   services: ['크라운/브릿지','임플란트 보철','금속도재'],                priceTable: [{ item: 'PFM 크라운', price: '110000', deliveryDays: 3 },{ item: '임플란트 크라운', price: '200000', deliveryDays: 4 }], compatibleScanners: ['Medit'],                          monthlyCapacity: 1400, remakeRate: 1.4 },
        // 8) identicon - capacity 없음
        { id: 'dummy-8',  _dummy: true, businessName: '부평한빛기공',       profileImageUrl: avIdenticon('bupyeong','ffdfbf'), address: '인천광역시 부평구 부평대로 39',    lat: 37.5072, lng: 126.7218, slogan: '빠른 납기 24시간',                  services: ['크라운/브릿지','임시 보철','지르코니아'],                  priceTable: [{ item: 'PMMA 임시치', price: '70000', deliveryDays: 1 },{ item: '지르코니아 크라운', price: '160000', deliveryDays: 2 }], compatibleScanners: ['Shining 3D','Medit'],            remakeRate: 1.3 },
        // 9) thumbs - 단가 0개
        { id: 'dummy-9',  _dummy: true, businessName: '김포중앙기공소',     profileImageUrl: avThumbs('gimpo'),                address: '경기도 김포시 김포한강8로 410',    lat: 37.6155, lng: 126.7158, slogan: '김포·검단 지역 거점',               services: ['크라운/브릿지','임플란트 보철','교정장치'],                priceTable: [], compatibleScanners: ['Medit','3Shape TRIOS'], monthlyCapacity: 950,  remakeRate: 1.6 },
        // 10) rings - 풀 데이터
        { id: 'dummy-10', _dummy: true, businessName: '성남덴티스타',       profileImageUrl: avRings('seongnam','b6e3f4'),     address: '경기도 성남시 분당구 정자일로 95', lat: 37.3585, lng: 127.1108, slogan: '디지털 워크플로우 · 당일 배송',     services: ['CAD/CAM 디지털 기공','지르코니아','임플란트 보철','올세라믹'], priceTable: [{ item: '지르코니아 크라운', price: '175000', deliveryDays: 2 },{ item: '임플란트 크라운', price: '210000', deliveryDays: 4 },{ item: '올세라믹', price: '245000', deliveryDays: 4 }], compatibleScanners: ['Primescan','3Shape TRIOS'], monthlyCapacity: 1800, remakeRate: 1.0 },
        // 11) initials - 빈약한 데이터
        { id: 'dummy-11', _dummy: true, businessName: '수원경기치과기공소', profileImageUrl: avInitials('수원','22c55e'),  address: '경기도 수원시 영통구 광교로 145',  lat: 37.2872, lng: 127.0563, slogan: '광교 신도시 인근',                  services: ['크라운/브릿지'],                                          priceTable: [{ item: '지르코니아 크라운', price: '170000', deliveryDays: 3 }], compatibleScanners: ['Medit'], monthlyCapacity: 1300 },
        // 12) shapes
        { id: 'dummy-12', _dummy: true, businessName: '부천새로치기공',     profileImageUrl: avShapes('bucheon','d1d4f9'),     address: '경기도 부천시 원미구 부천로 198',  lat: 37.5034, lng: 126.7660, slogan: '의치·총의치 전문 30년',             services: ['의치 제작/수리','금속도재','크라운/브릿지'],                priceTable: [{ item: '총의치', price: '420000', deliveryDays: 7 }], compatibleScanners: ['Medit'], monthlyCapacity: 700,  remakeRate: 2.0 },
        // 13) identicon - 다양한 단가
        { id: 'dummy-13', _dummy: true, businessName: '안양심미보철',       profileImageUrl: avIdenticon('anyang','c0aede'),   address: '경기도 안양시 만안구 안양로 320',  lat: 37.3943, lng: 126.9568, slogan: '심미·기능 모두 잡다',               services: ['올세라믹','라미네이트','지르코니아'],                       priceTable: [{ item: '올세라믹', price: '250000', deliveryDays: 4 },{ item: '라미네이트', price: '290000', deliveryDays: 5 },{ item: '지르코니아 크라운', price: '180000', deliveryDays: 3 }], compatibleScanners: ['3Shape TRIOS','Primescan'], monthlyCapacity: 1100, remakeRate: 1.0 },
        // 14) thumbs - 슬로건 없음
        { id: 'dummy-14', _dummy: true, businessName: '고양일산덴탈',       profileImageUrl: avThumbs('ilsan'),                address: '경기도 고양시 일산동구 중앙로 1219', lat: 37.6584, lng: 126.8320, slogan: '',                                  services: ['크라운/브릿지','임플란트 보철','커스텀 어버트먼트'],         priceTable: [{ item: '임플란트 크라운', price: '215000', deliveryDays: 4 }], compatibleScanners: ['Medit'], monthlyCapacity: 1250, remakeRate: 1.3 },
        // 15) rings - 부산
        { id: 'dummy-15', _dummy: true, businessName: '해운대블루덴탈',     profileImageUrl: avRings('haeundae','ffd5dc'),     address: '부산광역시 해운대구 해운대로 813', lat: 35.1631, lng: 129.1635, slogan: '부산·울산 거점',                    services: ['CAD/CAM 디지털 기공','지르코니아','투명교정'],              priceTable: [{ item: '지르코니아 크라운', price: '180000', deliveryDays: 3 },{ item: '투명교정', price: '380000', deliveryDays: 7 }], compatibleScanners: ['Primescan','3Shape TRIOS'], monthlyCapacity: 1600, remakeRate: 0.9 },
        // 16) initials - 대구
        { id: 'dummy-16', _dummy: true, businessName: '대구수성치기공',     profileImageUrl: avInitials('대구','f97316'),  address: '대구광역시 수성구 동대구로 1',     lat: 35.8580, lng: 128.6304, slogan: '대구·경북 전역 배송',               services: ['크라운/브릿지','임플란트 보철','교정장치','금속도재'],     priceTable: [{ item: 'PFM 크라운', price: '115000', deliveryDays: 3 }], compatibleScanners: ['Medit','iTero'], monthlyCapacity: 1450, remakeRate: 1.2 },
        // 17) shapes - 대전
        { id: 'dummy-17', _dummy: true, businessName: '대전유성정밀기공',   profileImageUrl: avShapes('yuseong','ffdfbf'),     address: '대전광역시 유성구 대학로 99',      lat: 36.3625, lng: 127.3563, slogan: '충청권 No.1 디지털 기공',           services: ['CAD/CAM 디지털 기공','지르코니아','임플란트 보철'],         priceTable: [{ item: '지르코니아 크라운', price: '170000', deliveryDays: 2 },{ item: '임플란트 크라운', price: '205000', deliveryDays: 4 }], compatibleScanners: ['Primescan','Medit'], monthlyCapacity: 1700, remakeRate: 0.8 },
        // 18) identicon - 광주
        { id: 'dummy-18', _dummy: true, businessName: '광주서구더치과기공', profileImageUrl: avIdenticon('gwangju','b6e3f4'), address: '광주광역시 서구 상무대로 760',     lat: 35.1525, lng: 126.8909, slogan: '호남권 정통 기공',                  services: ['크라운/브릿지','금속도재','임플란트 보철'],                priceTable: [{ item: 'PFM 크라운', price: '108000', deliveryDays: 3 }], compatibleScanners: ['Medit'], monthlyCapacity: 1000, remakeRate: 1.5 },
        // 19) thumbs - 울산
        { id: 'dummy-19', _dummy: true, businessName: '울산남구치과기공소', profileImageUrl: avThumbs('ulsan'),                address: '울산광역시 남구 삼산로 282',       lat: 35.5384, lng: 129.3293, slogan: '울산 산업 지역 특화',               services: ['임플란트 보철','크라운/브릿지','지르코니아'],              priceTable: [{ item: '임플란트 크라운', price: '210000', deliveryDays: 4 }], compatibleScanners: ['3Shape TRIOS'], remakeRate: 1.4 },
        // 20) rings - 천안
        { id: 'dummy-20', _dummy: true, businessName: '천안중앙치기공',     profileImageUrl: avRings('cheonan','c0aede'),      address: '충청남도 천안시 동남구 만남로 43', lat: 36.8151, lng: 127.1140, slogan: '천안·아산 전문',                    services: ['크라운/브릿지','지르코니아','의치 제작/수리'],              priceTable: [{ item: '지르코니아 크라운', price: '160000', deliveryDays: 3 },{ item: '총의치', price: '400000', deliveryDays: 7 }], compatibleScanners: ['Medit','Shining 3D'], monthlyCapacity: 950,  remakeRate: 1.3 },
    ];

    const loadLabs = async () => {
        try {
            setLoading(true);
            const adsRef = collection(db, 'labAdvertisements');

            // 활성화된 모든 기공소 전국 fetch (거리 무관)
            let labsData = [];
            const allSnap = await getDocs(query(adsRef, where('isActive', '==', true)));
            const seen = new Set();
            allSnap.docs.forEach(d => {
                if (!seen.has(d.id)) {
                    seen.add(d.id);
                    labsData.push({ id: d.id, ...d.data() });
                }
            });

            // 홍보 미공개 회원 기공소도 프리미엄에 포함 (2026-07-18) — users에서 기본정보로 노출
            try {
                const labUsersSnap = await getDocs(query(collection(db, 'users'), where('businessType', '==', 'lab')));
                labUsersSnap.docs.forEach(d => {
                    const u = d.data();
                    if (seen.has(d.id)) return;               // 이미 홍보페이지 있음
                    if (u.userType === 'staff') return;        // 직원 계정 제외
                    if (u.hideFromDirectory === true) return;  // 목록 숨김(테스트 계정 등)
                    if ((u.email || '').endsWith('@test.com')) return; // 테스트 계정 자동 제외
                    seen.add(d.id);
                    labsData.push({
                        id: d.id,
                        labId: d.id,
                        userId: d.id,
                        businessName: u.businessName || u.companyName || u.name || '기공소',
                        address: u.address || '',
                        profileImageUrl: u.profileImageUrl || '',
                        _noAd: true,   // 홍보 미작성 회원 (기본 카드)
                    });
                });
            } catch (e) {
                console.warn('회원 기공소(홍보 미공개) 로드 실패:', e);
            }

            // 리뷰 데이터 로드
            const reviewsSnap = await getDocs(collection(db, 'labReviews'));
            const reviewsByLab = {};
            reviewsSnap.docs.forEach(d => {
                const data = d.data();
                if (data.labId) {
                    if (!reviewsByLab[data.labId]) reviewsByLab[data.labId] = [];
                    reviewsByLab[data.labId].push(data);
                }
            });

            // 각 기공소에 리뷰 병합
            labsData.forEach(lab => {
                const labId = lab.labId || lab.userId || lab.id;
                const revs = reviewsByLab[labId] || [];
                lab._reviewCount = revs.length;
                if (revs.length > 0) {
                    lab._avgRating = (revs.reduce((s, r) => s + (r.rating || 0), 0) / revs.length).toFixed(1);
                }
            });

            // 최신 리뷰 (하단 리뷰 섹션용)
            const labNameById = {};
            labsData.forEach(l => { labNameById[l.labId || l.userId || l.id] = l.businessName; });
            const allReviews = reviewsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            allReviews.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setRecentReviews(allReviews.slice(0, 6).map(r => ({
                ...r, _labName: r.labName || labNameById[r.labId] || '기공소',
            })));

            // 더미 기공소는 다음 조건일 때만 노출 (분쟁·오인 방지)
            // 1) 비로그인 사용자에게만 (어차피 이름·사진·단가 블러됨)
            // 2) 실제 등록 기공소가 30개 미만일 때 (충분히 늘어나면 자동 제거)
            const showDummy = !isLoggedIn && !isAdminViewer && labsData.length < 30;

            // 더미마다 포트폴리오 이미지 개수를 다양하게 주입 (0·2·3·4·5 혼합)
            // → 카드마다 다른 분량의 썸네일이 보여 단조로움 해소
            const portfolioCounts = [0, 5, 3, 0, 4, 2, 5, 3, 0, 4, 2, 5, 3, 0, 4, 2, 5, 3, 0, 4];
            const dummyWithPortfolio = DUMMY_LABS.map((lab, idx) => {
                const cnt = portfolioCounts[idx] || 0;
                if (cnt === 0) return lab;
                return {
                    ...lab,
                    portfolioImages: Array.from({ length: cnt }, (_, i) =>
                        `https://picsum.photos/seed/dcluster-${lab.id}-${i}/400/300`
                    ),
                };
            });

            // 유료(가맹) 우선 정렬 + 그 후 일반
            const sortedLabsData = [...labsData].sort((a, b) => {
                const ap = a.subscriptionActive === true ? 1 : 0;
                const bp = b.subscriptionActive === true ? 1 : 0;
                return bp - ap;  // 유료 먼저
            });
            const allLabs = showDummy ? [...sortedLabsData, ...dummyWithPortfolio] : sortedLabsData;

            setLabs(allLabs);
            setFilteredLabs(allLabs);
            setVisibleCount(LAB_PAGE_SIZE);  // 페이징 초기화

            // LOCALDATA 미가입 기공소 (지도 마커용) — claimedBy null + 영업중만
            try {
                const ldSnap = await getDocs(query(
                    collection(db, 'labDirectories'),
                    where('isActive', '==', true)
                ));
                const ldData = ldSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                    .filter(d => !d.claimedBy);
                setUnclaimedLabs(ldData);
            } catch (e) {
                console.warn('LOCALDATA 기공소 로드 실패:', e);
            }
        } catch (error) {
            console.error('기공소 목록 로드 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    // ── 회원 기공소 ↔ 공공데이터(미가입) 중복 판정 (2026-07-18) ──
    // 상호: 공백·괄호·접미사(치과기공소/기공소/덴탈랩/랩) 변형 무시
    const normName = (n) => (n || '')
        .replace(/\s|\(.*?\)/g, '')
        .replace(/치과기공소|치과기공|기공소|덴탈랩|덴탈|랩$/g, '')
        .toLowerCase();
    // 주소: 공백 제거 후 앞 12자 비교 (시군구+도로명 수준)
    const normAddr = (a) => (a || '').replace(/\s/g, '').slice(0, 12);
    const isSameLab = (member, unclaimed) => {
        const mn = normName(member.businessName);
        const un = normName(unclaimed.name || unclaimed.businessName);
        const nameMatch = mn && un && (mn === un || mn.includes(un) || un.includes(mn));
        const addrMatch = normAddr(member.address) && normAddr(member.address) === normAddr(unclaimed.address);
        // 상호 일치 + 같은 시/도, 또는 주소 일치
        return (nameMatch && extractRegion(member.address) === extractRegion(unclaimed.address)) || addrMatch;
    };

    // 주소에서 시/도 추출
    const extractRegion = (address) => {
        if (!address) return '';
        const parts = address.trim().split(/\s+/);
        // "서울특별시" → "서울", "경기도" → "경기", "부산광역시" → "부산"
        const first = (parts[0] || '').replace(/특별시|광역시|도$/g, '');
        return first;
    };

    const userRegion = extractRegion(user?.address || '');

    const RADIUS_KM = 10;

    const filterLabsFn = useCallback(() => {
        // 1) 키워드/전문분야 1차 필터
        let filtered = labs;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(lab =>
                (lab.businessName || '').toLowerCase().includes(q) ||
                (lab.philosophy || lab.introduction || '').toLowerCase().includes(q) ||
                (lab.address || '').toLowerCase().includes(q) ||
                (lab.slogan || '').toLowerCase().includes(q)
            );
        }
        if (filterSpecialty !== 'all') {
            filtered = filtered.filter(lab => {
                const names = getServiceNames(lab);
                return names.some(n => n.includes(filterSpecialty) || filterSpecialty.includes(n));
            });
        }

        // 2) 유료(가맹) vs 무료(미가입) 분리
        const paidLabs = filtered.filter(l => l.subscriptionActive === true);
        const freeLabs = filtered.filter(l => l.subscriptionActive !== true);

        // 3) 거리 무관 — 모든 활성 기공소 노출 (유료 우선 + 거리순 정렬만)
        let result;
        if (userLocation) {
            const sortedPaid = [...paidLabs].sort((a, b) => {
                return getDistance(userLocation.lat, userLocation.lng, a.lat, a.lng)
                    - getDistance(userLocation.lat, userLocation.lng, b.lat, b.lng);
            });
            const sortedFree = freeLabs
                .map(l => ({ ...l, _distance: getDistance(userLocation.lat, userLocation.lng, l.lat, l.lng) }))
                .sort((a, b) => (a._distance ?? Infinity) - (b._distance ?? Infinity));
            result = [...sortedPaid, ...sortedFree];
        } else if (isLoggedIn && localFirst && userRegion) {
            // 위치 없을 때 fallback: 본인 지역 우선
            result = [...filtered].sort((a, b) => {
                const aMatch = extractRegion(a.address) === userRegion ? 0 : 1;
                const bMatch = extractRegion(b.address) === userRegion ? 0 : 1;
                return aMatch - bMatch;
            });
        } else {
            result = filtered;
        }

        setFilteredLabs(result);
        setVisibleCount(LAB_PAGE_SIZE);  // 필터 변경 시 페이징 리셋
    }, [labs, searchQuery, filterSpecialty, localFirst, userRegion, isLoggedIn, userLocation, getDistance]);

    useEffect(() => {
        loadLabs();

        // 방문 기록 (지역 정보 없이 유입 수만 기록)
        (async () => {
            try {
                await addDoc(collection(db, 'qrVisits'), {
                    labId: 'find-lab', labName: '기공소 찾기 페이지', visitedAt: serverTimestamp(),
                    referrer: (document.referrer || '').slice(0, 500), page: '/find-lab', // rules 500자 상한(2026-10-11)
                });
            } catch (e) { console.warn('[방문 기록] 저장 실패:', e?.code || e?.message); }
        })();
    }, []);
    useEffect(() => { filterLabsFn(); }, [filterLabsFn]);
    // 필터·정렬 변경 시 1페이지로
    useEffect(() => { setPage(1); setPremiumPage(1); }, [searchQuery, filterSpecialty, regionFilter, sortBy]);

    // 현재 지도 중심 기준 50km 재검색 (유료는 전국 그대로, 무료만 50km 재필터)
    const searchInViewport = useCallback(() => {
        const map = mapInstance.current;
        if (!map) return;
        const center = map.getCenter();
        const centerLat = center.getLat();
        const centerLng = center.getLng();
        const paidLabs = labs.filter(l => l.subscriptionActive === true);
        const freeLabs = labs.filter(l => l.subscriptionActive !== true);
        const sortedPaid = [...paidLabs].sort((a, b) => {
            return getDistance(centerLat, centerLng, a.lat, a.lng)
                - getDistance(centerLat, centerLng, b.lat, b.lng);
        });
        const sortedFree = freeLabs
            .map(l => ({ ...l, _distance: getDistance(centerLat, centerLng, l.lat, l.lng) }))
            .sort((a, b) => (a._distance ?? Infinity) - (b._distance ?? Infinity));
        const filtered = [...sortedPaid, ...sortedFree];
        setFilteredLabs(filtered);
        setVisibleCount(LAB_PAGE_SIZE);  // 지도 재검색 시 페이징 리셋
        setShowResearchBtn(false);
    }, [labs, getDistance]);

    // 카카오맵 초기화 — 전체화면 지도가 열렸을 때만
    useEffect(() => {
        if (!showMap || !mapRef.current) return;
        const kakao = window.kakao;
        if (!kakao?.maps) return;

        const initMap = () => {
            if (!mapRef.current) return;
            const container = mapRef.current;
            // 기존 인스턴스가 있으면 재사용 (재검색 시 사용자가 본 화면 유지)
            const map = mapInstance.current || new kakao.maps.Map(container, {
                center: new kakao.maps.LatLng(37.5665, 126.9780),
                level: 5,  // ~500m 반경 표시
            });
            mapInstance.current = map;

            // 마커 표시
            const geocoder = new kakao.maps.services.Geocoder();
            markersRef.current.forEach(m => m.setMap(null));
            markersRef.current = [];

            // 통합 popup 관리 (가입·미가입 모두) — 지도 클릭 시 닫힘
            let activePopup = null;
            const closeActivePopup = () => {
                if (activePopup) { activePopup.close(); activePopup = null; }
            };
            kakao.maps.event.addListener(map, 'click', closeActivePopup);

            // 지도 이동 → "현재 지도에서 검색" 버튼 노출
            kakao.maps.event.addListener(map, 'idle', () => {
                setShowResearchBtn(true);
            });

            filteredLabs.forEach(lab => {
                if (!lab.address) return;
                geocoder.addressSearch(lab.address, (result, status) => {
                    if (status === kakao.maps.services.Status.OK) {
                        const coords = new kakao.maps.LatLng(result[0].y, result[0].x);
                        // 유료 가입 기공소 = 황금 마커, 무료 가입 = 기본 보라
                        const isPaid = lab.subscriptionActive === true;
                        const marker = isPaid
                            ? new kakao.maps.Marker({
                                map, position: coords, zIndex: 5,
                                image: new kakao.maps.MarkerImage(
                                    'data:image/svg+xml;base64,' + btoa(`
                                        <svg xmlns='http://www.w3.org/2000/svg' width='40' height='52' viewBox='0 0 40 52'>
                                          <defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
                                            <stop offset='0%' stop-color='#fbbf24'/><stop offset='100%' stop-color='#8b5cf6'/>
                                          </linearGradient></defs>
                                          <path d='M20 0C9 0 0 9 0 20c0 14 20 32 20 32s20-18 20-32C40 9 31 0 20 0z' fill='url(#g)' stroke='#fff' stroke-width='2'/>
                                          <circle cx='20' cy='20' r='8' fill='#fff'/>
                                          <text x='20' y='25' text-anchor='middle' font-size='12' font-weight='900' fill='#f59e0b'>★</text>
                                        </svg>`),
                                    new kakao.maps.Size(40, 52),
                                    { offset: new kakao.maps.Point(20, 52) }
                                ),
                            })
                            : new kakao.maps.Marker({ map, position: coords, zIndex: 3 });
                        markersRef.current.push(marker);

                        const infoContent = isLoggedIn ? `
                            <div style="padding:12px 12px 10px 12px;font-size:13px;width:240px;line-height:1.5;font-family:'Apple SD Gothic Neo',sans-serif;word-break:keep-all;overflow-wrap:break-word;white-space:normal;box-sizing:border-box;">
                                ${isPaid ? '<span style="display:inline-block;padding:2px 7px;background:linear-gradient(135deg,#fbbf24,#f59e0b);color:#fff;border-radius:4px;font-size:9px;font-weight:900;margin-bottom:4px;">★ PRO</span><br/>' : ''}
                                <strong style="font-size:14px;color:${isPaid ? '#78350f' : '#0f172a'};">${lab.businessName || '기공소'}</strong>
                                ${lab.slogan ? `<br/><span style="color:#6366f1;font-size:11px;">${lab.slogan}</span>` : ''}
                                <br/><span style="color:#64748b;font-size:11px;">${lab.address}</span>
                            </div>` : `
                            <div style="padding:12px 12px 10px 12px;font-size:13px;width:200px;text-align:center;font-family:'Apple SD Gothic Neo',sans-serif;word-break:keep-all;overflow-wrap:break-word;white-space:normal;box-sizing:border-box;">
                                <strong style="font-size:14px;color:#94a3b8;">🔒 ○○기공소</strong>
                                <br/><span style="color:#64748b;font-size:11px;">회원가입 후 상세 정보 확인</span>
                            </div>`;
                        const infowindow = new kakao.maps.InfoWindow({ content: infoContent, removable: true });

                        kakao.maps.event.addListener(marker, 'click', () => {
                            closeActivePopup();
                            infowindow.open(map, marker);
                            activePopup = infowindow;
                            if (!isLoggedIn) setShowSignupPrompt(true);
                        });
                    }
                });
            });

            // 첫 진입 시에만 첫 기공소 중심으로 이동 (재검색 시에는 사용자가 본 화면 유지)
            if (!mapInitializedRef.current && !userLocation && filteredLabs.length > 0 && filteredLabs[0].address) {
                mapInitializedRef.current = true;
                geocoder.addressSearch(filteredLabs[0].address, (result, status) => {
                    if (status === kakao.maps.services.Status.OK) {
                        map.setCenter(new kakao.maps.LatLng(result[0].y, result[0].x));
                    }
                });
            } else if (!mapInitializedRef.current && userLocation) {
                mapInitializedRef.current = true;
                map.setCenter(new kakao.maps.LatLng(userLocation.lat, userLocation.lng));
            }

            // ── LOCALDATA 미가입 기공소 (회색 작은 마커, 전화번호 미노출) ──
            unclaimedMarkersRef.current.forEach(m => m.setMap(null));
            unclaimedMarkersRef.current = [];

            // 미가입 기공소 — 미리 변환된 geoPoint 우선 사용 (카카오 호출 절감)
            const drawUnclaimedMarker = (lab, lat, lng) => {
                const coords = new kakao.maps.LatLng(lat, lng);
                const dotImage = new kakao.maps.MarkerImage(
                    'data:image/svg+xml;base64,' + btoa(`
                        <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
                            <circle cx='14' cy='14' r='12' fill='#06b6d4' stroke='#fff' stroke-width='2.5' />
                            <text x='14' y='18.5' font-size='13' font-weight='900' fill='#fff' text-anchor='middle' font-family='-apple-system, sans-serif'>L</text>
                        </svg>`),
                    new kakao.maps.Size(28, 28),
                    { offset: new kakao.maps.Point(14, 14) }
                );
                const marker = new kakao.maps.Marker({ map, position: coords, image: dotImage, zIndex: 2 });
                unclaimedMarkersRef.current.push(marker);

                const popupContent = isLoggedIn ? `
                    <div style="padding:12px 12px 10px 12px;font-size:12px;width:240px;line-height:1.5;font-family:'Apple SD Gothic Neo',sans-serif;word-break:keep-all;overflow-wrap:break-word;white-space:normal;box-sizing:border-box;">
                        <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;padding-right:18px;">
                            <strong style="font-size:13px;color:#0f172a;flex:1;">${lab.name || '치과기공소'}</strong>
                            <span style="padding:2px 6px;background:#e5e7eb;color:#6b7280;border-radius:4px;font-size:9px;font-weight:800;flex-shrink:0;">미가입</span>
                        </div>
                        <div style="color:#64748b;font-size:11px;line-height:1.5;">${lab.address || ''}</div>
                        <div style="margin-top:8px;padding:6px 8px;background:#fef3c7;border-radius:6px;font-size:10px;color:#92400e;line-height:1.5;word-break:keep-all;overflow-wrap:break-word;">
                            ⓘ 우리 앱에 가입하지 않은 기공소입니다.<br/>가입 후 연락처·포트폴리오 확인 가능
                        </div>
                    </div>` : `
                    <div style="padding:12px 12px 10px 12px;font-size:12px;width:200px;text-align:center;font-family:'Apple SD Gothic Neo',sans-serif;word-break:keep-all;overflow-wrap:break-word;white-space:normal;box-sizing:border-box;">
                        <strong style="font-size:13px;color:#94a3b8;">🔒 ○○기공소</strong>
                        <br/><span style="color:#64748b;font-size:11px;">회원가입 후 상세 정보 확인</span>
                    </div>`;
                const popup = new kakao.maps.InfoWindow({ content: popupContent, removable: true });
                kakao.maps.event.addListener(marker, 'click', () => {
                    closeActivePopup();
                    popup.open(map, marker);
                    activePopup = popup;
                    if (!isLoggedIn) setShowSignupPrompt(true);
                });
            };

            const memberLabsForDedup = labs.filter(l => !l._dummy);
            unclaimedLabs.filter(l => !memberLabsForDedup.some(m => isSameLab(m, l))).forEach(lab => {
                // 1순위: 미리 저장된 위경도
                if (lab.geoPoint && lab.geoPoint.lat && lab.geoPoint.lng) {
                    drawUnclaimedMarker(lab, lab.geoPoint.lat, lab.geoPoint.lng);
                    return;
                }
                // 2순위: 주소 → 카카오 geocode (느림, 한도 있음)
                if (!lab.address) return;
                geocoder.addressSearch(lab.address, (result, status) => {
                    if (status !== kakao.maps.services.Status.OK) return;
                    drawUnclaimedMarker(lab, parseFloat(result[0].y), parseFloat(result[0].x));
                });
            });
        };

        // kakao.maps.load가 이미 완료된 경우 직접 실행, 아니면 load 콜백
        if (kakao.maps.LatLng) {
            setTimeout(initMap, 100); // DOM 렌더 후 실행
        } else {
            kakao.maps.load(initMap);
        }
    }, [filteredLabs, unclaimedLabs, showMap]);

    // 전체화면 지도 닫기 — 컨테이너가 언마운트되므로 인스턴스 초기화
    const closeMap = () => {
        setShowMap(false);
        mapInstance.current = null;
        mapInitializedRef.current = false;
        markersRef.current = [];
        unclaimedMarkersRef.current = [];
        setShowResearchBtn(false);
    };

    const handleCardClick = (lab) => {
        // 비로그인자: 가입 유도 모달만 노출, 상세 페이지 이동 차단
        if (!isLoggedIn) {
            setShowSignupPrompt(true);
            return;
        }
        // 더미 기공소는 상세 페이지 미존재 → 가입 유도 모달로 안내
        if (lab._dummy) {
            setShowSignupPrompt(true);
            return;
        }
        navigate(`/lab/${lab.labId || lab.id}`);
    };

    const handleConnectRequest = async (lab, e) => {
        e.stopPropagation();
        if (!isLoggedIn) {
            setShowSignupPrompt(true);
            return;
        }
        // 더미 기공소는 연결 요청 불가
        if (lab._dummy) {
            alert('해당 기공소는 미등록 상태입니다.\n다른 기공소를 선택해주세요.');
            return;
        }
        // 기공소 계정 uid (labAdvertisements의 userId/labId가 실제 사용자 문서 id)
        const labUid = lab.userId || lab.labId || lab.id;
        try {
            setConnectingLabId(labUid);
            // 거래처 관리(connections)와 동일한 방식 — 기존 연결/요청 상태 확인
            const ref = collection(db, 'connections');
            const [sentSnap, recvSnap] = await Promise.all([
                getDocs(query(ref, where('requesterId', '==', user.uid), where('receiverId', '==', labUid))),
                getDocs(query(ref, where('requesterId', '==', labUid), where('receiverId', '==', user.uid))),
            ]);
            const all = [...sentSnap.docs, ...recvSnap.docs].map(d => ({ id: d.id, ...d.data() }));
            if (all.some(c => c.status === 'accepted')) {
                alert('이미 거래처로 연결된 기공소입니다.');
                return;
            }
            if (sentSnap.docs.some(d => d.data().status === 'pending')) {
                alert('이미 연결 요청을 보낸 기공소입니다.\n기공소가 수락하면 [설정 > 거래처 관리]에서 확인됩니다.');
                return;
            }
            const recvPending = recvSnap.docs.find(d => d.data().status === 'pending');
            if (recvPending) {
                // 기공소가 먼저 요청해둔 상태 → 바로 수락
                const { updateDoc, doc } = await import('firebase/firestore');
                await updateDoc(doc(db, 'connections', recvPending.id), {
                    status: 'accepted', updatedAt: new Date(), acceptedBy: user.uid,
                });
                alert('기공소가 이미 연결 요청을 보낸 상태여서 바로 연결되었습니다!');
                return;
            }
            // 새 요청 — 회사 ID 기준 + 쌍 고정 ID (중복 차단)
            const ownerId = user.companyId || user.uid;
            const { setDoc, doc: docRef } = await import('firebase/firestore');
            await setDoc(docRef(db, 'connections', [ownerId, labUid].sort().join('_')), {
                requesterId: ownerId,
                receiverId: labUid,
                recipientId: labUid,
                status: 'pending',
                createdAt: new Date(),
                requesterName: user.businessName || user.companyName || user.name || '',
                requesterType: user.businessType || 'dental',
                requestedByUid: user.uid,
                source: 'find-lab',
            }, { merge: true });
            alert('연결 요청을 보냈습니다.\n기공소가 수락하면 [설정 > 거래처 관리]에서 확인됩니다.');
        } catch (err) {
            console.error('연결 요청 실패:', err);
            alert('연결 요청에 실패했습니다.');
        } finally {
            setConnectingLabId(null);
        }
    };

    if (loading) {
        return (
            <div style={S.loading}>
                <div style={S.spinner} />
                <p>기공소를 찾고 있습니다...</p>
            </div>
        );
    }

    // 🔒 치과·관리자·비로그인만 허용 — 그 외 모든 로그인 사용자 차단
    if (isBlockedUser) {
        const isLabUser = user?.businessType === 'lab';
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', background: '#f8fafc' }}>
                <div style={{ maxWidth: 440, width: '100%', background: '#fff', borderRadius: 20, padding: '40px 28px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                        <Lock size={28} color="#ef4444" />
                    </div>
                    <h2 style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginBottom: 10 }}>접근 제한</h2>
                    <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, marginBottom: 28 }}>
                        {isLabUser
                            ? <>기공소 회원은 기공소 찾기 페이지를<br />이용할 수 없습니다.</>
                            : <>기공소 찾기 페이지는 <strong style={{ color: '#0f172a' }}>치과 회원 전용</strong>입니다.<br />현재 계정으로는 이용하실 수 없습니다.</>
                        }
                        <br />
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>단가·포트폴리오 등 영업정보 보호를 위한 정책입니다.</span>
                    </p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {isLabUser && matchOpen && (
                            <button onClick={() => navigate('/lab-advertising')} style={{ padding: '12px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                내 홍보페이지
                            </button>
                        )}
                        <button onClick={() => navigate('/dashboard')} style={{ padding: '12px 20px', background: isLabUser ? '#fff' : '#6366f1', color: isLabUser ? '#475569' : '#fff', border: isLabUser ? '1px solid #e2e8f0' : 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            대시보드
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // =========================================================================
    // 카드
    // =========================================================================
    // 미가입(공공데이터) 기공소 — 기본정보만 노출하는 간단 카드
    const renderUnclaimedCard = (lab) => {
        const dist = formatDistance(lab);
        const blurStyle = !isLoggedIn ? { filter: 'blur(8px)', userSelect: 'none', pointerEvents: 'none' } : {};
        return (
            <div key={`ld-${lab.id}`} style={S.unclaimedCard} onClick={() => { if (!isLoggedIn) setShowSignupPrompt(true); }}>
                <div style={S.cardHeader}>
                    <div style={S.cardAvatar}><Building2 size={22} color="#94a3b8" /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ ...S.cardTitle, ...blurStyle }}>{!isLoggedIn ? '○○기공소' : (lab.name || lab.businessName || '치과기공소')}</h3>
                        <span style={S.unclaimedBadge}>미가입 · 기본정보</span>
                    </div>
                </div>
                <div style={S.cardBody}>
                    {lab.address && (
                        <div style={{ ...S.cardInfoRow, ...blurStyle }}>
                            <MapPin size={12} color="#94a3b8" /><span>{lab.address}</span>
                        </div>
                    )}
                    {dist && <span style={S.distBadge}>{dist}</span>}
                    <div style={S.unclaimedNote}>아직 덴탈클러스터에 가입하지 않은 기공소입니다</div>
                </div>
            </div>
        );
    };

    const renderLabCard = (lab) => {
        if (lab._unclaimed) return renderUnclaimedCard(lab);
        const services = getServiceNames(lab);
        const scanners = Array.isArray(lab.compatibleScanners) ? lab.compatibleScanners : [];
        // ⚠️ 가격표(priceTable)는 정책상 전면 비공개 — 카드·목록 어디에도 노출하지 않음

        // 비로그인: 이름·사진 블러 (지역·전문분야는 공개)
        const blurStyle = !isLoggedIn ? { filter: 'blur(8px)', userSelect: 'none', pointerEvents: 'none' } : {};
        const photoBlur = !isLoggedIn ? { filter: 'blur(14px)', userSelect: 'none', pointerEvents: 'none' } : {};
        const dist = formatDistance(lab);

        return (
            <div key={lab.id} style={S.card} onClick={() => handleCardClick(lab)}>
                {/* 프로필 헤더 — 비로그인: 이름·사진 블러 */}
                <div style={S.cardHeader}>
                    <div style={{ ...S.cardAvatar, ...photoBlur }}>
                        {lab.profileImageUrl ? (
                            <img src={lab.profileImageUrl} alt="" style={S.cardAvatarImg} />
                        ) : (
                            <Building2 size={22} color="#94a3b8" />
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ ...S.cardTitle, ...blurStyle }}>{!isLoggedIn ? '○○기공소' : (lab.businessName || '기공소')}</h3>
                        {lab.slogan && <p style={{ ...S.cardSlogan, ...blurStyle }}>{lab.slogan}</p>}
                    </div>
                </div>

                <div style={S.cardBody}>
                    {/* 거리 + 평점 */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                        {dist && <span style={S.distBadge}>{dist}</span>}
                        {lab._avgRating && (
                            <span style={S.cardReview}>
                                <Star size={12} fill="#f59e0b" color="#f59e0b" />
                                <span style={S.cardReviewScore}>{lab._avgRating}</span>
                                <span style={S.cardReviewCount}>({lab._reviewCount})</span>
                            </span>
                        )}
                    </div>

                    {/* 스펙 — 비로그인 블러 */}
                    <div style={{ ...S.cardSpecs, ...blurStyle }}>
                        {scanners.length > 0 && <span style={S.specBadge}>스캐너 {scanners.length}종</span>}
                        {lab.remakeRate && <span style={S.specBadge}>리메이크 {lab.remakeRate}%</span>}
                        {lab.monthlyCapacity && <span style={S.specBadge}>월 {lab.monthlyCapacity}건</span>}
                    </div>

                    {/* 첫 거래 혜택 — 로그인 시만 공개 */}
                    {lab.firstDealBenefit && isLoggedIn && (
                        <div style={S.cardBenefit}>🏷️ {lab.firstDealBenefit}</div>
                    )}

                    {/* 전문분야 — 비로그인 블러 */}
                    {services.length > 0 && (
                        <div style={{ ...S.cardTags, ...blurStyle }}>
                            {services.slice(0, 3).map((s, i) => <span key={i} style={S.cardTag}>{s}</span>)}
                            {services.length > 3 && <span style={S.cardTagMore}>+{services.length - 3}</span>}
                        </div>
                    )}

                    {/* 포트폴리오 — 비로그인 블러 */}
                    {Array.isArray(lab.portfolioImages) && lab.portfolioImages.length > 0 && (
                        <div style={{ ...S.cardPortfolio, ...photoBlur }}>
                            {lab.portfolioImages.slice(0, 3).map((url, i) => (
                                <div key={i} style={S.cardThumb}>
                                    <img src={url} alt="" style={S.cardThumbImg} />
                                </div>
                            ))}
                            {lab.portfolioImages.length > 3 && (
                                <div style={S.cardThumbMore}>
                                    <ImageIcon size={14} color="#64748b" />
                                    <span>+{lab.portfolioImages.length - 3}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 주소 — 블러 */}
                    {lab.address && <div style={{ ...S.cardInfoRow, ...blurStyle }}><MapPin size={12} color="#94a3b8" /><span>{lab.address}</span></div>}

                    {/* 버튼 */}
                    <div style={S.cardActions}>
                        {/* 연결요청은 상세페이지에서만 — 목록 카드에서는 상세보기만 노출 (2026-07-17) */}
                        <button style={S.btnPrimary} onClick={(e) => { e.stopPropagation(); handleCardClick(lab); }}><Eye size={14} />상세보기</button>
                    </div>
                </div>

                {/* 비로그인 오버레이 안내 */}
                {!isLoggedIn && !isLoggedIn && false && (
                    <div style={S.blurOverlay} onClick={(e) => { e.stopPropagation(); setShowSignupPrompt(true); }}>
                        <Lock size={20} color="#6366f1" />
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#6366f1' }}>가입 후 확인</span>
                    </div>
                )}
            </div>
        );
    };

    // =========================================================================
    // 목록 구성 — 프리미엄 / 일반(회원+미가입) 분리 + 필터 + 정렬 + 페이지네이션
    // =========================================================================
    const LabCoords = (l) => l.geoPoint?.lat ? { lat: l.geoPoint.lat, lng: l.geoPoint.lng } : { lat: l.lat, lng: l.lng };
    const withDistance = (l) => {
        const c = LabCoords(l);
        return { ...l, _distance: userLocation ? getDistance(userLocation.lat, userLocation.lng, c.lat, c.lng) : Infinity };
    };
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = (lab) => {
        if (!q) return true;
        const hay = [lab.businessName, lab.name, lab.address, lab.slogan, lab.philosophy, lab.introduction, ...getServiceNames(lab)]
            .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
    };
    const matchesRegion = (lab) => regionFilter === 'all' || extractRegion(lab.address) === regionFilter;
    const matchesSpec = (lab) => {
        if (filterSpecialty === 'all') return true;
        const names = getServiceNames(lab);
        return names.some(n => n.includes(filterSpecialty) || filterSpecialty.includes(n));
    };

    // ── 프리미엄 = 덴탈클러스터 회원 기공소 전체 · 가까운순 · 4×3(12개/페이지) ──
    const memberLabs = labs.filter(l => matchesSearch(l) && matchesRegion(l) && matchesSpec(l)).map(withDistance);
    const premiumAll = [...memberLabs].sort((a, b) => {
        if (sortBy === 'rating') return (parseFloat(b._avgRating) || 0) - (parseFloat(a._avgRating) || 0) || ((a._distance ?? Infinity) - (b._distance ?? Infinity));
        if (sortBy === 'reviews') return (b._reviewCount || 0) - (a._reviewCount || 0) || ((a._distance ?? Infinity) - (b._distance ?? Infinity));
        return (a._distance ?? Infinity) - (b._distance ?? Infinity);
    });
    const PREMIUM_PAGE_SIZE = 12; // 4열 × 3줄
    const premiumTotalPages = Math.max(1, Math.ceil(premiumAll.length / PREMIUM_PAGE_SIZE));
    const safePremiumPage = Math.min(premiumPage, premiumTotalPages);
    const premiumLabs = premiumAll.slice((safePremiumPage - 1) * PREMIUM_PAGE_SIZE, safePremiumPage * PREMIUM_PAGE_SIZE);

    // ── 전체 기공소 = 회원 제외(미가입 공공데이터만) · 가까운순 · 4×5(20개/페이지) ──
    // 미가입은 분야 정보가 없어 분야 필터 선택 시 제외
    const memberAll = labs.filter(l => !l._dummy);
    const unclaimedList = filterSpecialty === 'all'
        ? unclaimedLabs
            .filter(l => matchesSearch(l) && matchesRegion(l))
            .filter(l => !memberAll.some(m => isSameLab(m, l)))   // 회원과 동일 업체(상호·주소)는 제외
            .map(l => ({ ...withDistance(l), _unclaimed: true }))
        : [];
    let regularList = [...unclaimedList];
    regularList.sort((a, b) => {
        const d = (a._distance ?? Infinity) - (b._distance ?? Infinity);
        if (d !== 0 && isFinite(a._distance) && isFinite(b._distance)) return d;
        return (a.businessName || a.name || '').localeCompare(b.businessName || b.name || '');
    });
    const ALL_PAGE_SIZE = 20; // 4열 × 5줄
    const totalPages = Math.max(1, Math.ceil(regularList.length / ALL_PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageItems = regularList.slice((safePage - 1) * ALL_PAGE_SIZE, safePage * ALL_PAGE_SIZE);
    // 지역 옵션 — 전체 주소에서 시/도 추출
    const regionOptions = ['all', ...Array.from(new Set([...labs, ...unclaimedLabs].map(l => extractRegion(l.address)).filter(Boolean))).sort()];

    // 리뷰 등록 — 로그인 필요
    const submitReview = async () => {
        if (!isLoggedIn) { setShowSignupPrompt(true); return; }
        if (!reviewLabId) { alert('리뷰를 남길 기공소를 선택해주세요.'); return; }
        if (!reviewText.trim()) { alert('리뷰 내용을 입력해주세요.'); return; }
        const target = labs.find(l => (l.labId || l.userId || l.id) === reviewLabId);
        try {
            setReviewSubmitting(true);
            const doc = {
                labId: reviewLabId,
                labName: target?.businessName || '',
                clinicId: user.uid,
                clinicName: user.businessName || user.name || '',
                rating: reviewRating,
                comment: reviewText.trim(),
                source: 'find-lab',
                createdAt: serverTimestamp(),
            };
            await addDoc(collection(db, 'labReviews'), doc);
            setRecentReviews(prev => [{ ...doc, _labName: doc.labName || '기공소', createdAt: { seconds: Math.floor(Date.now() / 1000) } }, ...prev].slice(0, 6));
            setReviewText(''); setReviewLabId(''); setReviewRating(5);
            alert('리뷰가 등록되었습니다. 감사합니다!');
        } catch (e) {
            console.error('리뷰 등록 실패:', e);
            alert('리뷰 등록에 실패했습니다.');
        } finally {
            setReviewSubmitting(false);
        }
    };

    const mobileContainer = isMobile ? { padding: '12px' } : {};
    const mobileTitle = isMobile ? { fontSize: '22px' } : {};
    const mobileSearchRow = isMobile ? { flexDirection: 'column', gap: '8px' } : {};
    const mobileSearchBox = isMobile ? { minWidth: 'auto', padding: '10px 14px' } : {};
    const mobileFilterBox = isMobile ? { padding: '10px 14px' } : {};
    const mobileGrid = isMobile ? { gridTemplateColumns: '1fr', gap: '12px' } : {};
    const mobileMapHeight = isMobile ? 250 : 380;

    return (
        <div style={{ ...S.container, ...mobileContainer }}>
            {/* 헤더 */}
            <div style={S.pageHeader}>
                <h1 style={{ ...S.pageTitle, ...mobileTitle }}>기공소 찾기</h1>
                <p style={S.pageSub}>검증된 치과기공소를 찾아보세요</p>
            </div>

            {/* 검색 */}
            <div style={{ ...S.searchRow, ...mobileSearchRow }}>
                <div style={{ ...S.searchBox, ...mobileSearchBox }}>
                    <Search size={18} color="#64748b" />
                    <input type="text" placeholder={isMobile ? '기공소 검색...' : '기공소명, 지역, 서비스 등으로 검색...'} value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)} style={{ ...S.searchInput, fontSize: isMobile ? '14px' : '15px' }} />
                </div>
                <div style={{ ...S.filterBox, ...mobileFilterBox }}>
                    <MapPin size={16} color="#64748b" />
                    <select value={regionFilter} onChange={e => setRegionFilter(e.target.value)} style={{ ...S.filterSelect, fontSize: isMobile ? '13px' : '14px' }}>
                        {regionOptions.map(o => <option key={o} value={o}>{o === 'all' ? '전체 지역' : o}</option>)}
                    </select>
                </div>
                <div style={{ ...S.filterBox, ...mobileFilterBox }}>
                    <Filter size={16} color="#64748b" />
                    <select value={filterSpecialty} onChange={e => setFilterSpecialty(e.target.value)} style={{ ...S.filterSelect, fontSize: isMobile ? '13px' : '14px' }}>
                        {specialtyOptions.map(o => <option key={o} value={o}>{o === 'all' ? '전체 분야' : o}</option>)}
                    </select>
                </div>
                <div style={{ ...S.filterBox, ...mobileFilterBox }}>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...S.filterSelect, fontSize: isMobile ? '13px' : '14px' }}>
                        <option value="distance">가까운순</option>
                        <option value="rating">평점순</option>
                        <option value="reviews">리뷰많은순</option>
                    </select>
                </div>
                <button onClick={() => setShowMap(true)} style={S.mapBtn}>
                    <MapIcon size={16} /> 지도로 보기
                </button>
            </div>

            {/* 위치약관 v1.1 공고 */}
            <PolicyNoticeBanner noticeKey={LBS_TERMS_V11_KEY} fallback={POLICY_NOTICES[LBS_TERMS_V11_KEY]} dismissible />

            {/* 결과 수 */}
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <p style={{ ...S.resultCount, margin: 0, fontSize: isMobile ? '13px' : '14px' }}>
                    프리미엄 <strong>{premiumAll.length}</strong>곳 · 전체 <strong>{regularList.length}</strong>곳
                    {userLocation ? ' · 가까운 순' : ''}
                </p>
                {/* 위치 기능 — 버튼 클릭 시 안내창 동의 후에만 위치 사용(약관 v1.1 §7 정리) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" onClick={onLocationButton} disabled={locating}
                        style={{ padding: '6px 12px', background: locating ? '#cbd5e1' : '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: locating ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                        {locating ? '위치 확인 중...' : '현재 위치로 찾기'}
                    </button>
                    <a href="/location-terms" target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                        위치기반서비스 이용약관
                    </a>
                    <button type="button" onClick={revokeLocConsent}
                        style={{ padding: 0, background: 'none', border: 'none', fontSize: 11, color: '#94a3b8', textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        위치 이용 동의 철회
                    </button>
                </div>
            </div>
            {locNotice && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f9ff', color: '#075985', fontSize: 12, borderRadius: 8 }}>{locNotice}</div>
            )}
            {showLocConsent && (
                <LocationConsentModal title="현재 위치로 기공소 찾기" onAgree={agreeLocConsent} onDecline={declineLocConsent} />
            )}

            {/* ── 프리미엄 기공소 (덴탈클러스터 회원) — 4×3, 가까운순 ── */}
            {premiumAll.length > 0 && (
                <div style={S.premiumSection}>
                    <div style={S.premiumHead}>
                        <Star size={16} fill="#f59e0b" color="#f59e0b" />
                        <h2 style={S.premiumTitle}>프리미엄 기공소</h2>
                        <span style={S.premiumSub}>덴탈클러스터 회원 기공소</span>
                    </div>
                    <div style={{ ...S.grid, ...(isMobile ? mobileGrid : { gridTemplateColumns: 'repeat(4, 1fr)' }) }}>
                        {premiumLabs.map(lab => renderLabCard(lab))}
                    </div>
                    {premiumTotalPages > 1 && (
                        <div style={S.pagination}>
                            <button style={S.pageArrow} disabled={safePremiumPage <= 1} onClick={() => setPremiumPage(safePremiumPage - 1)}>
                                <ChevronLeft size={16} />
                            </button>
                            {Array.from({ length: premiumTotalPages }, (_, i) => i + 1).map(p => (
                                <button key={p} style={{ ...S.pageNum, ...(p === safePremiumPage ? S.pageNumActive : {}) }} onClick={() => setPremiumPage(p)}>
                                    {p}
                                </button>
                            ))}
                            <button style={S.pageArrow} disabled={safePremiumPage >= premiumTotalPages} onClick={() => setPremiumPage(safePremiumPage + 1)}>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── 전체 기공소 (회원 제외 · 미가입 기본정보) — 4×5, 가까운순 ── */}
            {regularList.length === 0 && premiumAll.length === 0 ? (
                <div style={S.empty}>
                    <Building2 size={48} color="#cbd5e1" />
                    <p style={S.emptyTitle}>조건에 맞는 기공소가 없습니다</p>
                    <p style={S.emptySub}>다른 검색어나 필터를 시도해보세요</p>
                </div>
            ) : (
                <div>
                    {regularList.length > 0 && (
                        <>
                            <h2 style={S.listSectionTitle}>전체 기공소 <span style={{ fontSize: 13, fontWeight: 500, color: '#94a3b8' }}>(회원 기공소 제외)</span></h2>
                            <div style={{ ...S.grid, ...(isMobile ? mobileGrid : { gridTemplateColumns: 'repeat(4, 1fr)' }) }}>
                                {pageItems.map(lab => renderLabCard(lab))}
                            </div>
                            {/* 페이지네이션 */}
                            {totalPages > 1 && (
                                <div style={S.pagination}>
                                    <button style={S.pageArrow} disabled={safePage <= 1} onClick={() => { setPage(safePage - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                                        <ChevronLeft size={16} />
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
                                        .reduce((acc, p, idx, arr) => {
                                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                                            acc.push(p);
                                            return acc;
                                        }, [])
                                        .map((p, i) => p === '…' ? (
                                            <span key={`e${i}`} style={S.pageEllipsis}>…</span>
                                        ) : (
                                            <button
                                                key={p}
                                                style={{ ...S.pageNum, ...(p === safePage ? S.pageNumActive : {}) }}
                                                onClick={() => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    <button style={S.pageArrow} disabled={safePage >= totalPages} onClick={() => { setPage(safePage + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ── 최신 리뷰 + 리뷰 작성 ── */}
            <div style={S.reviewSection}>
                <div style={S.reviewCols}>
                    <div style={S.reviewListCol}>
                        <h2 style={S.reviewSectionTitle}><Star size={16} fill="#f59e0b" color="#f59e0b" /> 최신 리뷰</h2>
                        {recentReviews.length === 0 ? (
                            <p style={{ fontSize: 13, color: '#94a3b8' }}>아직 등록된 리뷰가 없습니다. 첫 리뷰를 남겨보세요!</p>
                        ) : (
                            recentReviews.map((r, i) => (
                                <div key={r.id || i} style={S.reviewItem}>
                                    <div style={S.reviewItemHead}>
                                        <strong style={{ fontSize: 13, color: '#0f172a' }}>{isLoggedIn ? r._labName : '○○기공소'}</strong>
                                        <span style={S.reviewStars}>{'★'.repeat(Math.round(r.rating || 0))}{'☆'.repeat(5 - Math.round(r.rating || 0))}</span>
                                    </div>
                                    <p style={S.reviewComment}>{r.comment || r.content || ''}</p>
                                    <span style={S.reviewMeta}>{r.clinicName ? `${(r.clinicName || '').slice(0, 1)}** 치과` : '치과 회원'}</span>
                                </div>
                            ))
                        )}
                    </div>
                    <div style={S.reviewFormCol}>
                        <h2 style={S.reviewSectionTitle}>리뷰 작성</h2>
                        <select value={reviewLabId} onChange={e => setReviewLabId(e.target.value)} style={S.reviewSelect}>
                            <option value="">거래한 기공소 선택</option>
                            {labs.filter(l => !l._dummy).map(l => (
                                <option key={l.id} value={l.labId || l.userId || l.id}>{isLoggedIn ? (l.businessName || '기공소') : '○○기공소'}</option>
                            ))}
                        </select>
                        <div style={S.starRow}>
                            {[1, 2, 3, 4, 5].map(n => (
                                <button key={n} style={S.starBtn} onClick={() => setReviewRating(n)}>
                                    <Star size={22} fill={n <= reviewRating ? '#f59e0b' : 'none'} color={n <= reviewRating ? '#f59e0b' : '#cbd5e1'} />
                                </button>
                            ))}
                        </div>
                        <textarea
                            value={reviewText}
                            onChange={e => setReviewText(e.target.value)}
                            placeholder={isLoggedIn ? '기공소와의 거래 경험을 남겨주세요' : '리뷰 작성은 로그인 후 가능합니다'}
                            style={S.reviewTextarea}
                            rows={4}
                        />
                        <button style={S.reviewSubmit} onClick={submitReview} disabled={reviewSubmitting}>
                            {reviewSubmitting ? '등록 중...' : (isLoggedIn ? '리뷰 등록' : '로그인하고 리뷰 남기기')}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── 전체화면 지도 오버레이 ── */}
            {showMap && (
                <div style={S.mapOverlay}>
                    <div style={S.mapOverlayHead}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MapIcon size={18} color="#6366f1" /> 지도에서 기공소 찾기
                        </span>
                        <button onClick={closeMap} style={S.mapCloseBtn}>✕ 닫기</button>
                    </div>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                        {showResearchBtn && (
                            <button
                                onClick={searchInViewport}
                                style={{
                                    position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                                    zIndex: 100, display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '8px 16px', background: '#fff', color: '#6366f1',
                                    border: '2px solid #6366f1', borderRadius: 999,
                                    fontSize: 13, fontWeight: 800, cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', whiteSpace: 'nowrap',
                                }}
                            >
                                <Search size={14} />
                                현재 지도에서 검색
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* 회원가입 유도 모달 */}
            {showSignupPrompt && (
                <div style={S.overlay} onClick={() => setShowSignupPrompt(false)}>
                    <div style={S.signupModal} onClick={e => e.stopPropagation()}>
                        <Lock size={40} color="#6366f1" />
                        <h2 style={{ margin: '16px 0 8px', fontSize: '20px', fontWeight: '700' }}>무료 회원가입</h2>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>가입하면 기공소 이름, 포트폴리오, 상세 정보를<br/>모두 확인할 수 있습니다.</p>
                        <p style={{ color: '#10b981', fontSize: '13px', fontWeight: '600', marginBottom: '20px' }}>가입비 무료 · 30초 완료</p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={async () => { try { await signOut(auth); } catch(e){} window.location.href = '/login'; }} style={S.btnLogin}><LogIn size={16} />로그인</button>
                            <button onClick={async () => { try { await signOut(auth); } catch(e){} window.location.href = '/signup'; }} style={S.btnSignup}><UserPlus size={16} />무료 가입</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    container: { maxWidth: '1400px', margin: '0 auto', padding: '20px' },
    loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', color: '#64748b' },
    spinner: { width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },

    pageHeader: { marginBottom: '28px' },
    pageTitle: { margin: '0 0 6px', fontSize: '28px', fontWeight: '800', color: '#0f172a' },
    pageSub: { margin: 0, fontSize: '15px', color: '#64748b' },
    resultCount: { margin: '0 0 16px', fontSize: '14px', color: '#94a3b8' },

    searchRow: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '280px', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', backgroundColor: '#fff', border: '2px solid #e2e8f0', borderRadius: '12px' },
    searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '15px', color: '#0f172a' },
    filterBox: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', backgroundColor: '#fff', border: '2px solid #e2e8f0', borderRadius: '12px' },
    filterSelect: { border: 'none', outline: 'none', fontSize: '14px', color: '#0f172a', fontWeight: '600', cursor: 'pointer', backgroundColor: 'transparent' },

    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' },

    // 카드 (컴팩트, 단가 중심)
    card: { position: 'relative', backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', height: '100%' },
    cardHeader: { display: 'flex', gap: '12px', alignItems: 'center', padding: '14px 14px 0' },
    cardAvatar: { width: '48px', height: '48px', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #e2e8f0' },
    cardAvatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    cardTitle: { margin: '0', fontSize: '15px', fontWeight: '700', color: '#0f172a', lineHeight: '1.3' },
    cardSlogan: { margin: '2px 0 0', fontSize: '11px', color: '#6366f1', fontWeight: '500' },
    cardBody: { padding: '10px 14px 14px', display: 'flex', flexDirection: 'column', flex: 1 },
    cardPriceRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' },
    cardPrice: { fontSize: '12px', color: '#334155', fontWeight: '500' },
    cardDays: { fontSize: '11px', color: '#94a3b8', fontWeight: '400' },
    cardSpecs: { display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px' },
    specBadge: { padding: '2px 7px', backgroundColor: '#f1f5f9', color: '#64748b', borderRadius: '4px', fontSize: '11px', fontWeight: '600' },
    cardBenefit: { padding: '6px 10px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '11px', fontWeight: '600', color: '#92400e', marginBottom: '8px' },
    cardTags: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' },
    cardTag: { padding: '2px 7px', backgroundColor: '#eef2ff', color: '#6366f1', borderRadius: '4px', fontSize: '10px', fontWeight: '600' },
    cardTagMore: { padding: '2px 7px', backgroundColor: '#f1f5f9', color: '#94a3b8', borderRadius: '4px', fontSize: '10px', fontWeight: '600' },
    cardPortfolio: { display: 'flex', gap: '4px', marginBottom: '8px' },
    cardThumb: { width: '56px', height: '56px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, backgroundColor: '#f1f5f9' },
    cardThumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
    cardThumbMore: { width: '56px', height: '56px', borderRadius: '8px', backgroundColor: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', fontSize: '11px', fontWeight: '600', color: '#64748b' },
    cardReview: { display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' },
    cardReviewScore: { fontSize: '13px', fontWeight: '700', color: '#f59e0b' },
    cardReviewCount: { fontSize: '12px', color: '#94a3b8' },
    cardInfoRow: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#94a3b8', marginBottom: '10px' },
    cardActions: { display: 'flex', gap: '6px', marginTop: 'auto', paddingTop: '10px' },
    btnPrimary: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '8px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    btnConnect: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '8px 12px', backgroundColor: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    btnSignupCard: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px', backgroundColor: '#eef2ff', color: '#6366f1', border: '2px solid #c7d2fe', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
    blurOverlay: { position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.1)', cursor: 'pointer' },

    empty: { textAlign: 'center', padding: '80px 20px', backgroundColor: '#fff', borderRadius: '16px', border: '2px dashed #e2e8f0' },
    emptyTitle: { marginTop: '16px', fontSize: '18px', fontWeight: '700', color: '#0f172a' },
    emptySub: { fontSize: '14px', color: '#94a3b8' },

    // 회원가입 유도 모달
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' },

    btnLogin: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    btnSignup: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', backgroundColor: '#fff', color: '#6366f1', border: '2px solid #6366f1', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },

    signupModal: { backgroundColor: '#fff', borderRadius: '20px', padding: '40px', maxWidth: '400px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
    viewToggle: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' },

    // ── 리뉴얼 추가 스타일 ──
    mapBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '14px 18px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
    distBadge: { padding: '2px 8px', backgroundColor: '#ecfdf5', color: '#059669', borderRadius: 999, fontSize: 11, fontWeight: 700 },

    premiumSection: { marginBottom: 28, padding: '20px', background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '1.5px solid #fde68a', borderRadius: 16 },
    premiumHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 },
    premiumTitle: { margin: 0, fontSize: 18, fontWeight: 800, color: '#78350f' },
    premiumSub: { fontSize: 12, color: '#b45309' },
    listSectionTitle: { margin: '0 0 14px', fontSize: 18, fontWeight: 800, color: '#0f172a' },

    unclaimedCard: { position: 'relative', backgroundColor: '#fafafa', borderRadius: 12, overflow: 'hidden', border: '1px dashed #d4d4d8', cursor: 'pointer', boxShadow: 'none' },
    unclaimedBadge: { display: 'inline-block', marginTop: 2, padding: '2px 8px', backgroundColor: '#e5e7eb', color: '#6b7280', borderRadius: 4, fontSize: 10, fontWeight: 800 },
    unclaimedNote: { marginTop: 8, padding: '6px 10px', backgroundColor: '#f1f5f9', borderRadius: 6, fontSize: 11, color: '#94a3b8' },

    pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '28px 0 8px', flexWrap: 'wrap' },
    pageArrow: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', color: '#475569' },
    pageNum: { minWidth: 36, height: 36, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#475569', cursor: 'pointer' },
    pageNumActive: { backgroundColor: '#6366f1', borderColor: '#6366f1', color: '#fff' },
    pageEllipsis: { padding: '0 4px', color: '#94a3b8', fontSize: 14 },

    reviewSection: { marginTop: 40, paddingTop: 32, borderTop: '1px solid #e2e8f0' },
    reviewCols: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 },
    reviewListCol: {},
    reviewFormCol: { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20, alignSelf: 'start' },
    reviewSectionTitle: { display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 16px', fontSize: 17, fontWeight: 800, color: '#0f172a' },
    reviewItem: { padding: '12px 14px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 10 },
    reviewItemHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 },
    reviewStars: { fontSize: 12, color: '#f59e0b', letterSpacing: 1 },
    reviewComment: { margin: '0 0 6px', fontSize: 13, color: '#475569', lineHeight: 1.6 },
    reviewMeta: { fontSize: 11, color: '#94a3b8' },
    reviewSelect: { width: '100%', padding: '11px 12px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, color: '#0f172a', marginBottom: 12, backgroundColor: '#fff' },
    starRow: { display: 'flex', gap: 2, marginBottom: 12 },
    starBtn: { background: 'none', border: 'none', padding: 4, cursor: 'pointer' },
    reviewTextarea: { width: '100%', padding: '11px 12px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, color: '#0f172a', resize: 'vertical', boxSizing: 'border-box', marginBottom: 12, fontFamily: 'inherit' },
    reviewSubmit: { width: '100%', padding: '13px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' },

    mapOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 900, backgroundColor: '#fff', display: 'flex', flexDirection: 'column' },
    mapOverlayHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#fff' },
    mapCloseBtn: { padding: '9px 16px', backgroundColor: '#f1f5f9', color: '#334155', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

export default PublicLabDirectory;
